import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Sticky, StickyProvider } from 'react-stickup';
import { graphql } from 'gatsby';
import dayjs from 'dayjs';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import SuggestedBlogs from '../components/SuggestedBlogs';

const Post = ({
  data: {
    post,
    blog: { posts = [] },
  },
  ...props
}) => {
  const container = React.createRef();

  const scrollIndicator = useCallback(() => {
    const blogScroll = document.getElementById('blog-main');
    const winScroll =
      document.body.scrollTop || document.documentElement.scrollTop;
    const height =
      blogScroll.scrollHeight - document.documentElement.clientHeight;
    const scrolled = (winScroll / height) * 100;
    document.getElementById('progress-bar').style.width = `${scrolled}%`;
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', scrollIndicator);

    return () => {
      window.removeEventListener('scroll', scrollIndicator);
    };
  }, [scrollIndicator]);

  return (
    <Layout {...props}>
      <SEO title={post.frontmatter.title} />
      <Sticky className="zindex" container={container}>
        <div id="progress-bar" />
      </Sticky>
      <section className="blog-single-main">
        <div className="container" id="blog-main">
          <div
            ref={container}
            className="col col-8 offset-2"
            style={{ position: 'relative' }}>
            <div className="pos-abs">
              <StickyProvider>
                <Sticky container={container} defaultOffsetTop={120}>
                  <div className="floating-social-icons">
                    <a
                      href="https://www.facebook.com/Powerplay-App-111342077401751"
                      rel="noreferrer noopener"
                      target="_blank">
                      <img src="/img/facebook.svg" alt="facebook" />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/getpowerplay/"
                      rel="noreferrer noopener"
                      target="_blank">
                      <img src="/img/linkedin-footer.svg" alt="twitter" />
                    </a>
                    <a
                      href="https://www.instagram.com/powerplay_app/"
                      rel="noreferrer noopener"
                      target="_blank">
                      <img src="/img/instagram.svg" alt="instagram" />
                    </a>
                  </div>
                </Sticky>
              </StickyProvider>
            </div>
            <div className="blog-single-content">
              <h1>{post.frontmatter.title}</h1>
              <p className="big-txt">
                by&nbsp;
                <span style={{ textDecoration: 'underline' }}>
                  {post.frontmatter.author}
                </span>
                &nbsp;on {dayjs(post.frontmatter.date).format('MMMM DD, YYYY')}
              </p>
              <div className="post-feature-image">
                <img
                  src={`/img/blog/${post?.frontmatter?.featuredImage}`}
                  alt="Blog title"
                />
              </div>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: post.html }} />
            </div>
          </div>
        </div>
      </section>
      <section className="pad100 bg-lightblue">
        <SuggestedBlogs posts={posts} />
      </section>
    </Layout>
  );
};

Post.propTypes = {
  data: PropTypes.shape({
    post: PropTypes.shape({
      html: PropTypes.string.isRequired,
      frontmatter: PropTypes.shape({
        title: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        author: PropTypes.string.isRequired,
        featuredImage: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    blog: PropTypes.shape({
      posts: PropTypes.arrayOf(
        PropTypes.shape({
          fields: PropTypes.shape({
            slug: PropTypes.string.isRequired,
          }).isRequired,
          frontmatter: PropTypes.shape({
            title: PropTypes.string.isRequired,
            featuredImage: PropTypes.string.isRequired,
          }).isRequired,
          excerpt: PropTypes.string.isRequired,
          id: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }).isRequired,
  }).isRequired,
};

export default Post;

export const query = graphql`
  query PostQuery($slug: String!) {
    post: markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      frontmatter {
        title
        date(formatString: "MM/DD/YYYY")
        author
        featuredImage
      }
    }
    blog: allMarkdownRemark(
      limit: 3
      filter: { fields: { slug: { ne: $slug } } }
    ) {
      posts: nodes {
        frontmatter {
          date(fromNow: true)
          title
          author
          featuredImage
        }
        excerpt
        id
        fields {
          slug
        }
      }
    }
  }
`;
